import Fuse, { type Expression } from "fuse.js";
import { compact } from "lodash-es";

import type { CollectionGridItem } from "./collection-grid-item";
import type { CollectionSort, CollectionStatusFilter } from "./collection-grid-options";
import { compareCollectionItems } from "./compare-collection-items";
import { toFuseExactSearchPattern } from "./to-fuse-exact-search-pattern";

const fuzzySearchKeys = [
  { name: "entity.title", weight: 0.45 },
  { name: "entity.subtitle", weight: 0.3 },
  { name: "entity.year", weight: 0.1 },
  { name: "entity.genres", weight: 0.075 },
  { name: "entity.styles", weight: 0.075 },
] as const;

export function filterAndSortCollectionItems(input: {
  artist: string | null;
  artistCountry: string | null;
  genre: string | null;
  items: CollectionGridItem[];
  locale: string;
  query: string;
  sort: CollectionSort;
  status: CollectionStatusFilter;
  style: string | null;
}): CollectionGridItem[] {
  const query = input.query.trim();
  const queryClause = query
    ? { $or: fuzzySearchKeys.map(({ name }) => ({ [name]: `"${query}"` })) }
    : null;
  const artistClause = input.artist
    ? { "entity.subtitle": toFuseExactSearchPattern(input.artist) }
    : null;
  const artistCountryClause = input.artistCountry
    ? { artistCountryCode: toFuseExactSearchPattern(input.artistCountry) }
    : null;
  const genreClause = input.genre
    ? { "entity.genres": toFuseExactSearchPattern(input.genre) }
    : null;
  const styleClause = input.style
    ? { "entity.styles": toFuseExactSearchPattern(input.style) }
    : null;
  const clauses = compact<Expression>([
    queryClause,
    artistClause,
    artistCountryClause,
    genreClause,
    styleClause,
  ]);
  const searchExpression: Expression = clauses.length ? { $and: clauses } : "";
  const statusFiltered = input.items.filter((item) => {
    if (input.status === "all") return true;
    if (input.status === "owned") return item.member.inLists.includes("collection");
    if (input.status === "wishlisted") return item.member.inLists.includes("wishlist");
    return item.member.inLists.length === 0;
  });
  const searched = new Fuse(statusFiltered, {
    ignoreLocation: true,
    keys: [...fuzzySearchKeys, "artistCountryCode"],
    minMatchCharLength: 1,
    threshold: 0.35,
    useExtendedSearch: true,
  })
    .search(searchExpression)
    .map((result) => result.item);
  const collator = new Intl.Collator(input.locale, { numeric: true, sensitivity: "base" });
  return searched.sort((left, right) => compareCollectionItems(left, right, input.sort, collator));
}
